import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Input, Label, Textarea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Card';
import { useServiceDetail } from '../../hooks/useServices';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import type { ServicePlan } from '@bharat-consultancy/shared';

export default function BookingForm() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading } = useServiceDetail(slug);
  const [selectedPlan, setSelectedPlan] = useState<ServicePlan | null>(null);

  const [form, setForm] = useState({
    contactName: '',
    contactEmail: '',
    contactMobile: '',
    contactWhatsapp: '',
    callbackDate: '',
    callbackTime: '',
    requirementSummary: '',
    additionalNotes: '',
    termsAccepted: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.service.plans && planId) {
      const plan = data.service.plans.find((p) => p.id === planId);
      if (plan) setSelectedPlan(plan);
    }
  }, [data, planId]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        contactName: user.name ?? f.contactName,
        contactEmail: user.email ?? f.contactEmail,
      }));
    }
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (!data || !selectedPlan) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Plan Not Found</h1>
        <p className="mt-2 text-ink/60">Please go back and select a service plan.</p>
      </div>
    );
  }

  const { service } = data;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedPlan) return; // guarded by the early return above, but narrowed again here for the closure
    if (!form.termsAccepted) {
      setError('Please accept the terms to proceed.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await api.orders.create({
        serviceId: service.id,
        servicePlanId: selectedPlan.id,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactMobile: form.contactMobile,
        contactWhatsapp: form.contactWhatsapp || undefined,
        callbackDate: form.callbackDate || undefined,
        callbackTime: form.callbackTime || undefined,
        requirementSummary: form.requirementSummary || undefined,
        additionalNotes: form.additionalNotes || undefined,
        termsAccepted: form.termsAccepted,
      });
      navigate(`/booking/${service.slug}/summary?orderId=${res.order.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <SEOHead title={`Book ${service.title} — Bharat Consultancy`} description="" canonicalPath={`/booking/${service.slug}`} />

      <section className="bg-paper py-10">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <BookingSteps current={2} />

          <div className="mt-8 rounded-lg border border-line bg-white p-6">
            <h1 className="font-display text-2xl font-bold text-ink">{service.title}</h1>
            <p className="mt-1 text-sm text-ink/60">
              {selectedPlan.planName} — ₹{selectedPlan.price.toLocaleString('en-IN')}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contactName">Full Name</Label>
                  <Input
                    id="contactName"
                    required
                    value={form.contactName}
                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contactEmail">Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    required
                    value={form.contactEmail}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contactMobile">Mobile Number</Label>
                  <Input
                    id="contactMobile"
                    type="tel"
                    required
                    value={form.contactMobile}
                    onChange={(e) => setForm({ ...form, contactMobile: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contactWhatsapp">WhatsApp Number (optional)</Label>
                  <Input
                    id="contactWhatsapp"
                    type="tel"
                    value={form.contactWhatsapp}
                    onChange={(e) => setForm({ ...form, contactWhatsapp: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="callbackDate">Preferred Callback Date</Label>
                  <Input
                    id="callbackDate"
                    type="date"
                    value={form.callbackDate}
                    onChange={(e) => setForm({ ...form, callbackDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="callbackTime">Preferred Callback Time</Label>
                  <Input
                    id="callbackTime"
                    type="time"
                    value={form.callbackTime}
                    onChange={(e) => setForm({ ...form, callbackTime: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="requirementSummary">Requirement Summary</Label>
                <Textarea
                  id="requirementSummary"
                  rows={3}
                  placeholder="Briefly describe what you need help with"
                  value={form.requirementSummary}
                  onChange={(e) => setForm({ ...form, requirementSummary: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="additionalNotes">Additional Notes (optional)</Label>
                <Textarea
                  id="additionalNotes"
                  rows={2}
                  value={form.additionalNotes}
                  onChange={(e) => setForm({ ...form, additionalNotes: e.target.value })}
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-ink/70">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.termsAccepted}
                  onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })}
                />
                I accept the terms of service and authorize Bharat Consultancy to process my request.
              </label>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Continue to Order Summary'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export function BookingSteps({ current }: { current: number }) {
  const steps = ['Service', 'Details', 'Summary', 'Payment', 'Done'];
  return (
    <div className="flex items-center justify-between">
      {steps.map((label, i) => {
        const step = i + 1;
        const isActive = step === current;
        const isDone = step < current;
        return (
          <div key={label} className="flex flex-1 items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
                  isDone
                    ? 'bg-ledger-green text-white'
                    : isActive
                    ? 'bg-seal-blue text-white'
                    : 'bg-line text-ink/40'
                }`}
              >
                {isDone ? '✓' : step}
              </div>
              <span className="text-[11px] text-ink/50">{label}</span>
            </div>
            {step < steps.length && <div className={`mx-1 h-px flex-1 ${isDone ? 'bg-ledger-green' : 'bg-line'}`} />}
          </div>
        );
      })}
    </div>
  );
}
